import { appDialogConfirm } from '@components'
import { useAuth, usePotentialHighQuality } from '@stateApp'
import { Archive, CheckCheck, Star, Trash, X } from 'lucide-solid'
import { Show, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { Flex } from 'styled-system/jsx'
import { useSingle } from '../../state'
import { ActionButton } from './ActionButton'
import { ScoreDialog } from './ScoreDialog'
import { SingalPhq } from './SignalPhq'

export const AdminActions = (props: { size?: 'admin' | 'mobileAdmin' }) => {
  const size = () => props.size || 'admin'
  const {
    onArchiveSetup,
    onDeleteSetup,
    setupQuery,
    setupPotentialHigQualityQuery,
    refreshPhq,
  } = useService(useSingle)()
  const { canSignalPhq, canManagePhq, canModifySetup, isSuper } = useService(useAuth)()
  const { confirmPhqMutation, rejectPhqMutation, scorePhqMutation } = useService(usePotentialHighQuality)()
  const [isOpen, setIsOpen] = createSignal(false)
  const [isPhqScoreOpen, setIsPhqScoreOpen] = createSignal(false)

  const canDelete = () => canModifySetup(setupQuery.data?.created_by)
  const phqRecord = () => setupPotentialHigQualityQuery.data
  const isPendingPHQ = () => phqRecord()?.status === 'pending'
  const isConfirmedPHQ = () => phqRecord()?.status === 'confirmed'

  const signalHeading = () => {
    if (isConfirmedPHQ()) return 'PHQ Confirmed'
    return phqRecord() ? 'PHQ Signaled' : 'Signal PHQ'
  }

  const onConfirmPhq = () => {
    const currentSetupId = setupQuery?.data?.id
    if (!currentSetupId) return
    appDialogConfirm(
      () => confirmPhqMutation.mutate(currentSetupId, {
        onSuccess: (data) => {
          if (data?.success) {
            setIsPhqScoreOpen(true)
            refreshPhq()
          }
        }
      }),
      'Confirm this setup as Potential High Quality?',
    )()
  }

  const onRejectPhq = () => {
    const currentSetupId = setupQuery?.data?.id
    if (!currentSetupId) return
    appDialogConfirm(
      () => rejectPhqMutation.mutate(currentSetupId, {
        onSuccess: () => refreshPhq(),
      }),
      'Reject this PHQ? Any PHQ score will be removed from this setup.',
    )()
  }

  const onScorePhq = (score: number) => {
    const currentSetupId = setupQuery?.data?.id
    if (!currentSetupId) return
    scorePhqMutation.mutate({ setupId: currentSetupId, score }, {
      onSuccess: () => {
        setIsPhqScoreOpen(false)
        refreshPhq()
      },
    })
  }

  return (
    <>
      <SingalPhq
        isOpen={isOpen()}
        setIsOpen={setIsOpen}
      />
      <ScoreDialog
        title="PHQ Score"
        isOpen={isPhqScoreOpen()}
        setIsOpen={setIsPhqScoreOpen}
        initialScore={phqRecord()?.score}
        loading={scorePhqMutation.isPending}
        onConfirm={onScorePhq}
      />
      <Show when={canSignalPhq() || canDelete()}>
        <Flex gap="5px" flexWrap="wrap">
          <Show when={canSignalPhq()}>
            <ActionButton
              size={size()}
              heading={signalHeading()}
              tooltip="Signal Potential High Quality"
              disabled={!!phqRecord()}
              onClick={() => setIsOpen(true)}
              icon={<Star />}
            />
          </Show>
          <Show when={canManagePhq() && isPendingPHQ()}>
            <ActionButton
              size={size()}
              heading="Confirm PHQ"
              tooltip="Confirm Potential High Quality"
              disabled={confirmPhqMutation.isPending}
              onClick={onConfirmPhq}
              icon={<CheckCheck />}
            />
          </Show>
          <Show when={canManagePhq() && isConfirmedPHQ()}>
            <ActionButton
              size={size()}
              heading={phqRecord()?.score ? 'Edit PHQ Score' : 'Score PHQ'}
              tooltip="Set the PHQ score for this setup"
              active={!!phqRecord()?.score}
              onClick={() => setIsPhqScoreOpen(true)}
              icon={<Star />}
            />
          </Show>
          <Show when={canManagePhq() && phqRecord()}>
            <ActionButton
              size={size()}
              heading="Reject PHQ"
              tooltip="Reject this PHQ so the setup can be signaled again"
              danger
              disabled={rejectPhqMutation.isPending}
              onClick={onRejectPhq}
              icon={<X />}
            />
          </Show>
          <Show when={isSuper()}>
            <ActionButton
              size={size()}
              heading="Archive"
              tooltip="Archive this setup"
              onClick={appDialogConfirm(
                onArchiveSetup,
                'Are you sure you want to archive this Setup?',
              )}
              icon={<Archive />}
            />
          </Show>
          <Show when={canDelete()}>
            <ActionButton
              size={size()}
              heading="Delete"
              tooltip="Delete this setup"
              danger
              onClick={appDialogConfirm(
                onDeleteSetup,
                'Are you sure you want to delete this Setup?',
              )}
              icon={<Trash />}
            />
          </Show>
        </Flex>
      </Show>
    </>
  )
}
